import api from '@/api/axiosApi'
import type {
  CalendarHeaderCreate,
  CalendarHeaderRead,
  CalendarHeaderUpdate,
  CalendarDetailResponse,
  CalendarEventCreate,
  CalendarEventRead,
  CalendarEventUpdate,
  CalendarEventSummary,
  CalendarMilestoneCreate,
  CalendarMilestoneRead,
  CalendarMilestoneUpdate,
  CalendarGridResponse,
  MilestoneRuleRead,
  PaginatedCalendarsResponse,
  PlanCalendarResponse,
  ReorderRequest,
} from '@/types'

// ─── Calendar Headers ─────────────────────────────────────────────

export interface ListCalendarsParams {
  search?: string
  is_active?: boolean
  skip?: number
  limit?: number
}

export async function listCalendars(
  params: ListCalendarsParams = {}
): Promise<PaginatedCalendarsResponse> {
  const { search, is_active, skip = 0, limit = 20 } = params
  const response = await api.get<PaginatedCalendarsResponse>('/calendars', {
    params: {
      search: search || undefined,
      is_active: is_active ?? undefined,
      skip,
      limit,
    },
    headers: { 'Cache-Control': 'no-cache' },
  })
  return response.data
}

export async function getCalendar(id: string): Promise<CalendarDetailResponse> {
  const response = await api.get<CalendarDetailResponse>(`/calendars/${id}`)
  return response.data
}

export async function createCalendar(
  payload: CalendarHeaderCreate
): Promise<CalendarHeaderRead> {
  const response = await api.post<CalendarHeaderRead>('/calendars', payload)
  return response.data
}

export async function updateCalendar(
  id: string,
  payload: CalendarHeaderUpdate
): Promise<CalendarHeaderRead> {
  const response = await api.patch<CalendarHeaderRead>(
    `/calendars/${id}`,
    payload
  )
  return response.data
}

export async function deleteCalendar(id: string): Promise<void> {
  await api.delete(`/calendars/${id}`)
}

export async function copyCalendar(
  id: string,
  name: string
): Promise<CalendarHeaderRead> {
  const response = await api.post<CalendarHeaderRead>(`/calendars/${id}/copy`, {
    name,
  })
  return response.data
}

// ─── Calendar Events ──────────────────────────────────────────────

export async function createCalendarEvent(
  calendarId: string,
  payload: CalendarEventCreate
): Promise<CalendarEventRead> {
  const response = await api.post<CalendarEventRead>(
    `/calendars/${calendarId}/events`,
    payload
  )
  return response.data
}

export async function updateCalendarEvent(
  calendarId: string,
  eventId: string,
  payload: CalendarEventUpdate
): Promise<CalendarEventRead> {
  const response = await api.patch<CalendarEventRead>(
    `/calendars/${calendarId}/events/${eventId}`,
    payload
  )
  return response.data
}

export async function deleteCalendarEvent(
  calendarId: string,
  eventId: string
): Promise<void> {
  await api.delete(`/calendars/${calendarId}/events/${eventId}`)
}

export async function reorderCalendarEvents(
  calendarId: string,
  payload: ReorderRequest
): Promise<void> {
  await api.patch(`/calendars/${calendarId}/events/reorder`, payload)
}

// ─── Calendar Milestones ──────────────────────────────────────────

export async function createMilestone(
  calendarId: string,
  eventId: string,
  payload: CalendarMilestoneCreate
): Promise<CalendarMilestoneRead> {
  const response = await api.post<CalendarMilestoneRead>(
    `/calendars/${calendarId}/events/${eventId}/milestones`,
    payload
  )
  return response.data
}

export async function updateMilestone(
  calendarId: string,
  eventId: string,
  msId: string,
  payload: CalendarMilestoneUpdate
): Promise<CalendarMilestoneRead> {
  const response = await api.patch<CalendarMilestoneRead>(
    `/calendars/${calendarId}/events/${eventId}/milestones/${msId}`,
    payload
  )
  return response.data
}

export async function deleteMilestone(
  calendarId: string,
  eventId: string,
  msId: string
): Promise<void> {
  await api.delete(
    `/calendars/${calendarId}/events/${eventId}/milestones/${msId}`
  )
}

export async function reorderMilestones(
  calendarId: string,
  eventId: string,
  payload: ReorderRequest
): Promise<void> {
  await api.patch(
    `/calendars/${calendarId}/events/${eventId}/milestones/reorder`,
    payload
  )
}

// ─── Grid View ────────────────────────────────────────────────────

export async function getCalendarGrid(
  calendarId: string
): Promise<CalendarGridResponse> {
  const response = await api.get<CalendarGridResponse>(
    `/calendars/${calendarId}/grid`
  )
  return response.data
}

// ─── Milestone Rules ──────────────────────────────────────────────

export async function listMilestoneRules(): Promise<MilestoneRuleRead[]> {
  const response = await api.get<MilestoneRuleRead[]>('/milestone-rules')
  return response.data
}

// ─── Plan Calendar ───────────────────────────────────────────────

export async function getPlanCalendar(
  planId: string,
  isSeasonal: boolean = false
): Promise<PlanCalendarResponse> {
  const response = await api.get<PlanCalendarResponse>(
    `/plans/${planId}/calendar`,
    { params: { is_seasonal: isSeasonal } }
  )
  return response.data
}

export async function assignPlanCalendar(
  planId: string,
  calendarEventId: string,
  isSeasonal: boolean = false
): Promise<PlanCalendarResponse> {
  const response = await api.post<PlanCalendarResponse>(
    `/plans/${planId}/calendar`,
    { calendar_event_id: calendarEventId, is_seasonal: isSeasonal }
  )
  return response.data
}

export async function listCalendarEvents(
  calendarId: string
): Promise<CalendarEventSummary[]> {
  const response = await api.get<CalendarEventSummary[]>(
    `/calendars/${calendarId}/events`
  )
  return response.data
}

export async function detachPlanCalendar(
  planId: string,
  isSeasonal: boolean = false
): Promise<void> {
  await api.delete(`/plans/${planId}/calendar`, {
    params: { is_seasonal: isSeasonal },
  })
}
